import { FC } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Stack, Tooltip, Typography } from '@mui/material';

import dayjs from 'dayjs';

import { NotificationResponse } from '@/api/data-contracts';
import { SEVERITY_ACCENT_COLORS } from '@/features/notifications/ui/Calendar/shared/Calendar.constants';

import useNotificationsStore from '../../model/notifications';
import { getNotificationIcon } from './NotificationDrawerCard.icons';
import useNotificationContent from './useNotificationContent';

interface Props {
  notification: NotificationResponse;
  pendingOrdersCount?: number;
  onNavigate?: () => void;
}

const NotificationDrawerCard: FC<Props> = ({
  notification,
  pendingOrdersCount = 0,
  onNavigate,
}) => {
  const showReadNotifications = useNotificationsStore((s) => s.showReadNotifications);
  const { title, description, link, markAsRead, open } = useNotificationContent(notification, {
    pendingOrdersCount,
    onNavigate,
  });

  const accentColor = SEVERITY_ACCENT_COLORS[notification.severity ?? 'COMMON'];

  const renderDescription = () => {
    const parts = description.split(/(#?\d+[A-Z]+\d+)/);
    if (parts.length === 1) return description;
    return parts.map((part, i) =>
      /^#?\d+[A-Z]+\d+$/.test(part) ? (
        <span key={`${i}-${part}`} style={{ textDecoration: 'underline' }}>
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const notificationDate = dayjs(notification.audit.timestamp);
  const isToday = notificationDate.isSame(dayjs(), 'day');
  const time = isToday ? (
    notificationDate.format('HH:mm')
  ) : (
    <>
      {notificationDate.format('D MMM')}{' '}
      <span style={{ fontSize: '0.375rem', lineHeight: 0, position: 'relative', top: '-0.1rem' }}>
        ●
      </span>{' '}
      {notificationDate.format('HH:mm')}
    </>
  );

  return (
    <Box
      sx={{
        position: 'relative',
        pt: '0.5rem',
        flexShrink: 0,
        '& > .dismiss-btn': { display: 'none' },
        '&:hover > .dismiss-btn': { display: 'flex' },
      }}
    >
      {!showReadNotifications && notification.status === 'UNREAD' && (
        <Box
          className="dismiss-btn"
          onClick={(e) => {
            e.stopPropagation();
            markAsRead();
          }}
          sx={{
            position: 'absolute',
            top: 0,
            right: '-0.5rem',
            width: '1.25rem',
            height: '1.25rem',
            borderRadius: '9999px',
            bgcolor: '#fefefe',
            filter: 'drop-shadow(0px 1px 4px rgba(0,0,0,0.15))',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 3,
          }}
        >
          <CloseIcon sx={{ fontSize: '0.75rem', color: '#615e6f' }} />
        </Box>
      )}
      <Stack
        direction="row"
        alignItems="center"
        gap="0.625rem"
        onClick={open}
        data-testid="notification-drawer-card"
        data-notification-type={notification.audit.type}
        sx={{
          position: 'relative',
          bgcolor: '#fefefe',
          filter: 'drop-shadow(0px 1px 4px rgba(0,0,0,0.15))',
          borderRadius: '0.625rem',
          height: '3.8125rem',
          pr: '0.5rem',
          py: '0.5rem',
          cursor: link ? 'pointer' : 'default',
          opacity: notification.status === 'READ' ? 0.65 : 1,
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: '0.25rem',
            height: '2.739rem',
            bgcolor: accentColor,
            borderRadius: '0 6.25rem 6.25rem 0',
            flexShrink: 0,
          }}
        />

        <Box sx={{ flexShrink: 0, display: 'flex', color: '#807D93' }}>
          {getNotificationIcon(notification.audit.type)}
        </Box>

        <Typography
          sx={{
            position: 'absolute',
            top: '0.5rem',
            right: '0.5rem',
            fontSize: '0.75rem',
            color: '#615e6f',
            fontFamily: 'Neue Frutiger World',
            fontWeight: 400,
            lineHeight: 1.6,
            whiteSpace: 'nowrap',
          }}
        >
          {time}
        </Typography>

        <Stack flex={1} minWidth={0} justifyContent="center" height="2.8125rem">
          <Typography
            noWrap
            sx={{
              fontSize: '0.75rem',
              color: '#615e6f',
              fontFamily: 'Neue Frutiger World',
              fontWeight: 400,
              lineHeight: 1.6,
              pr: '5.5rem',
            }}
          >
            {title}
          </Typography>

          <Tooltip title={description} enterDelay={300} disableInteractive>
            <Typography
              noWrap
              sx={{
                fontSize: '0.625rem',
                color: '#615e6f',
                fontFamily: 'Neue Frutiger World',
                fontWeight: 400,
                lineHeight: 1.6,
                alignSelf: 'stretch',
              }}
            >
              {renderDescription()}
            </Typography>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
};

export { getNotificationIcon } from './NotificationDrawerCard.icons';

export default NotificationDrawerCard;
